import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import DatePicker from 'react-datepicker';
import { withRouter } from 'react-router-dom';
import { Badge } from 'react-bootstrap';
import {
    Button,
    Switch,
    FormControlLabel
} from '@sumologic/yama-ds-components';

import { keymap } from '@codemirror/view';
import { EditorSelection } from '@codemirror/state';
// import { EditorView, ViewUpdate } from '@codemirror/view';

import { filterBarMode } from '~/components/CodeEditor/modes/filterBarMode';
import CodeEditor from '~/components/CodeEditor/CodeEditor';
import Select from '~/components/Select/Select';

import MiniModal from '~/components/MiniModal/MiniModal';
import ToggleButtons from '~/components/ToggleButtons/ToggleButtons';

import PreferencesContext from '~/containers/Preferences/PreferencesContext';
import StickyAlertContext from '~/containers/StickyAlertManager/StickyAlertContext';

import { useIntlTranslate } from '~/modules/helpers/lang';
import { formatAsUserShortDateTime } from '~/modules/helpers/date';

import OperatorsHelper from './OperatorsHelper';
import TimeSliders from './TimeSliders';

/*
const RE_Tokens = /\sand\s|\sor\s|[^a-zA-Z0-9]/gi; // ><|…|!|!=|\?|>|<|>=|<=|=
const RE_AND = /\sand\s/g;
const RE_OR = /\sor\s/g;
*/

const RE_24h = /\d{2}:\d{2}:?\d*\s?(AM|PM)?/gi;
const RE_time = /\d+[-\/]\d+[-\/]\d+/g;
const RE_azAZ = /[a-zA-Z]/;

const key_TimeInterval = 'Time Interval';
const key_DatePicker = 'Date Picker';

const HDWMY = { H: 1, D: 0, W: 0, M: 0, Y: 0 };

class InputBar extends React.Component {
    static contextType = PreferencesContext;

    constructor(props) {
        super(props);
        this.translate = (str, values) =>
            useIntlTranslate(props.intl.formatMessage, str, values || null);
        this.state = {
            inputNewValue: props.filterValue ?? '',
            dateTimeSelection: key_TimeInterval,
            timeInterval: { ...HDWMY }
            /*
            selectedTime: null,
            selectedDate: null,
            inputDisabled: false,
            absoluteToggle: false,            
            showOperatorsHelper: false,
            showMiniModal: false,
            timeToggle: false,
            */
        };
    }

    // todo: to remove
    setEditorValue = str => {
        console.trace(str);
        const strLen = str?.length || 0;
        const oldStr = this.state.inputNewValue;
        const oldStrLen = oldStr?.length || 0;
        const index = strLen < oldStrLen ? strLen : oldStrLen;
        /**-/
        console.dir(this.editorView);
        console.dir({ ...this.state });
        console.log({
            index,
            str,
            strLen,
            oldStr,
            oldStrLen
        });
        /**/
        this.editorView?.dispatch({
            changes: {
                from: 0,
                to: index,
                insert: str
            }
        });
    };

    setEditorCaretPosition = index => {
        //RN: try to fix "Selection points outside of document" for ex. New Widget Modal in Dashboard
        // todo https://discuss.codemirror.net/t/rangeerror-selection-points-outside-of-document/5556
        setTimeout(() => {
            try {
                this.editorView?.dispatch({
                    selection: EditorSelection.cursor(index ?? 0)
                });
            } catch (exception) {
                if (APP_INFO.debug) {
                    console.debug(exception);
                }
            }
        });
    };

    //------------------------------------------------------------------------------------------------------------------

    notify = params => {
        if (params.message) {
            this.addStickyAlert(params);
        }
    };

    notifyWarn = params => {
        if (!params.message) {
            params.message = this.translate('Something went wrong');
        }
        this.notify({
            ...params,
            status: 'warning'
        });
    };

    notifyHelperWarn_filterEmpty = () => {
        this.notifyWarn({
            title: this.translate('Helper'),
            message: this.translate(
                'Filter string is empty, add value to permit the comparison'
            )
        });
    };

    //------------------------------------------------------------------------------------------------------------------

    cleanAfterTime = () => {
        this.setState(
            {
                showMiniModal: false,
                absoluteToggle: false,
                timeInterval: { ...HDWMY }
            },
            () => {
                this.focus();
                this.props.setActivateDateModals(false);
            }
        );
    };

    quickPick = time => {
        const str = this.state.inputNewValue;
        if (str) {
            const [a, b] = this.state.absoluteToggle ? ['|', '|'] : ['[', ']'];
            const val = `${str} >= #${a}${time}${b}`;
            this.setState(
                {
                    inputNewValue: val
                },
                () => {
                    // todo: review
                    //RN: evaluate update also in filterBar or only in filterBar component
                    this.props.setFilterValue(val);
                    this.cleanAfterTime();
                }
            );
        } else {
            this.notifyHelperWarn_filterEmpty();
        }
    };

    setDateTimeFilter = () => {
        const { selectedDate, selectedTime } = this.state;
        // prettier-ignore
        if (key_TimeInterval !== this.state.dateTimeSelection) {
            const offset = CURRENT_USER.offset2num;
            const getIsoStringSliced = (n, a, b) => new Date((n + offset) * 1e3).toISOString().slice(a, b);
            let onlyDate = '';
            if (selectedDate?.getTime) {
                const ms = selectedDate.getTime() / 1e3 + offset; // todo review
                const val = formatAsUserShortDateTime(ms);
                onlyDate =
                    RE_azAZ.test(val)
                        ? getIsoStringSliced(ms, 0, 10)
                        : val.replace(RE_24h, '').trim();
            }

            let onlyTime = '';
            if (selectedTime?.getTime) {
                const ms = selectedTime.getTime() / 1e3;
                const val = formatAsUserShortDateTime(ms);
                onlyTime =
                    RE_azAZ.test(val)
                        ? getIsoStringSliced(ms, 11, 16)
                        : val.replace(RE_time, '').trim();
            }

            this.inputChangeFromModal(`${onlyDate} ${onlyTime}`, () => {
                this.setState({
                    selectedDate: null,
                    selectedTime: null,
                    timeToggle: false,
                    showMiniModal: false,
                    timeInterval: {...HDWMY}
                });
                this.props.setActivateDateModals(false);
            });
        } else {
            const [a, b] = this.state.absoluteToggle ? ['|', '|'] : ['[', ']'];
            const values = ['#', a];
            const interval = this.state.timeInterval;
            for (const letter of ['Y', 'M', 'W', 'D', 'H']) {
                const value = interval[letter];
                // noinspection EqualityComparisonWithCoercionJS
                if (0 != value) {
                    values.push(value, letter, '\u0020');
                }
            }
            const dateString = values.join('').trim() + b;
            this.inputChangeFromModal(
                dateString.length > 3 ? dateString : '',
                this.cleanAfterTime
            );
        }
        if (!this.state.inputNewValue) {
            this.notifyHelperWarn_filterEmpty();
        }
    };

    inputChangeFromModal = (stringToAdd, callback) => {
        let val = this.state.inputNewValue;
        if (val) {
            // this.editorView
            const selectionInfo = this.editorSelectionInfo;
            if (selectionInfo) {
                const { from, to } = selectionInfo;
                if (from === to) {
                    // cursor position
                    if (0 !== from) {
                        if (to !== val.length) {
                            // prettier-ignore
                            val = `${val.slice(0, from)?.trimEnd()}\u0020${stringToAdd}${val.slice(to)}`;
                        } else {
                            val = `${val?.trimEnd()}\u0020${stringToAdd}`;
                        }
                    } else {
                        val = `${stringToAdd}${val?.trimStart()}`;
                    }
                } else {
                    // has selection
                    val = `${val
                        .slice(0, from)
                        ?.trimEnd()}\u0020${stringToAdd}${val.slice(to)}`;
                }
            } else {
                val = `${stringToAdd}${val}`;
            }
            this.setState(
                {
                    inputNewValue: val
                },
                () => {
                    callback?.(val);
                    //RN: evaluate update also in filterBar or only in filterBar component
                    this.props.setFilterValue(val);
                }
            );
        } else {
            // todo allow date?
            this.notifyHelperWarn_filterEmpty();
        }
    };

    onUpdate = viewUpdate => {
        const { from = 0, to = 0 } = viewUpdate.state.selection.ranges[0] || {};
        this.editorSelectionInfo = { from, to };
    };

    onChange = value => {
        // console.debug('onChange', value);
        let finalValue = value;

        // todo: review
        if (finalValue) {
            const lc = finalValue.toLowerCase();

            [' ,', ' and '].forEach(replaceString => {
                if (lc.includes(replaceString)) {
                    finalValue = finalValue.replace(
                        replaceString,
                        '\u0020AND\u0020'
                    );
                }
            });

            [' +', ' or '].forEach(replaceString => {
                if (lc.includes(replaceString)) {
                    finalValue = finalValue.replace(
                        replaceString,
                        '\u0020OR\u0020'
                    );
                }
            });
        }

        this.setState(
            {
                inputNewValue: finalValue
            },
            () => {
                this.props.handleChange(this.state.inputNewValue);
            }
        );
    };

    onCreateEditor = (editorView, editorState) => {
        this.props.getEditorApi?.({ editorView, editorState });
        this.editorView = editorView;
    };

    focus = () => {
        this.editorView?.focus();
    };

    sectionOptionsCreation = () => {
        return this.props.sectionOptions.map(el => {
            return {
                value: el.preferencesTag,
                label: el.section
            };
        });
    };

    //------------------------------------------------------------------------------------------------------------------

    componentDidUpdate(prevProps, prevState) {
        /**-/
        console.log('𝘐𝘯𝘱𝘶𝘵𝘉𝘢𝘳 𝙘𝙤𝙢𝙥𝙤𝙣𝙚𝙣𝙩𝘿𝙞𝙙𝙐𝙥𝙙𝙖𝙩𝙚', {
            prevProps,
            currProps: this.props,
            prevState,
            currState: { ...this.state }
        });
        /**/
        const val = this.props.filterValue;
        if (this.editorView && prevProps.section !== this.props.section) {
            //RN: if prev filterValue is different from current, there is another condition below 'section if' statement
            if (val === prevProps.filterValue) {
                // this.setEditorValue('');
                this.setState(
                    {
                        inputNewValue: ''
                    },
                    () => {
                        this.props.setFilterValue('');
                        this.setEditorCaretPosition(0);
                        this.focus();
                    }
                );
            }
        }
        if (val !== prevProps.filterValue) {
            //this.setEditorValue(this.props.filterValue);
            this.setState(
                {
                    inputNewValue: val
                },
                () => {
                    this.setEditorCaretPosition(
                        this.state.inputNewValue?.length ?? 0
                    );
                    this.focus();
                }
            );
        }
        const shownHelpModals = this.state.showOperatorsHelper;
        if (this.props.activateHelpModals) {
            if (!shownHelpModals) {
                this.setState({
                    showMiniModal: false,
                    showOperatorsHelper: true
                });
            }
        } else {
            if (shownHelpModals) {
                this.setState({
                    showMiniModal: false,
                    showOperatorsHelper: false
                });
            }
        }
        const shownDateModals = this.state.showMiniModal;
        if (shownDateModals) {
            if (!this.props.activateDateModals) {
                this.setState(
                    {
                        timeToggle: false,
                        showMiniModal: false,
                        timeInterval: { ...HDWMY }
                    },
                    () => {
                        this.props.setActivateDateModals(false);
                        this.setState({
                            showMiniModal: false
                        });
                    }
                );
            }
            if (!this.state.inputDisabled) {
                this.setState({
                    inputDisabled: true
                });
            }
        } else {
            if (this.props.activateDateModals) {
                this.setState({
                    showMiniModal: true,
                    showOperatorsHelper: false
                });
            }
        }
    }

    componentWillUnmount() {
        this.editorSelectionInfo = null;
    }

    render() {
        let sectionLabel = this.props.section || 'global';
        if ('objects' === sectionLabel) {
            sectionLabel = 'entities';
        }
        return (
            <PreferencesContext.Consumer>
                {context => (
                    <StickyAlertContext.Consumer>
                        {stickyContext => {
                            this.addStickyAlert = stickyContext.addStickyAlert;
                            const extensions = [
                                //TODO RN: ask if I should map others key combinations like shift-enter and similar
                                keymap.of([
                                    {
                                        key: 'Enter',
                                        run: view => {
                                            const syntheticEvent = {
                                                target: view.dom,
                                                key: 'Enter'
                                            };
                                            this.props.handleKeyPress(
                                                syntheticEvent
                                            );
                                        }
                                    }
                                ])
                            ];
                            const editorCompletions = this.props.completions;
                            const editorSetup = {
                                lineNumbers: false,
                                autocompletion: editorCompletions?.length > 0
                                /*
                                completionKeymap: false,
                                lintKeymap: false,
                                highlightActiveLineGutter: false,
                                allowMultipleSelections: false,
                                history: false,
                                foldGutter: false,
                                dropCursor: false,
                                indentOnInput: false,
                                crosshairCursor: false,
                                highlightActiveLine: false,
                                closeBracketsKeymap: false,
                                defaultKeymap: false,
                                searchKeymap: false,
                                historyKeymap: false,
                                foldKeymap: false,
                                highlightSelectionMatches: true,
                                highlightSpecialChars: true,
                                rectangularSelection: true,
                                drawSelection: true,
                                syntaxHighlighting: true,
                                bracketMatching: true,
                                closeBrackets: true
                                */
                            };
                            return (
                                <>
                                    <div
                                        id="input-bar"
                                        data-testid="input-bar"
                                        className="form-control bar-background input-bar"
                                    >
                                        <span className="relative-section">
                                            {this.props.sectionOptions ? (
                                                <Select
                                                    data-id="select-section"
                                                    className="relative-section-select"
                                                    innerClassNames={{
                                                        menuPortal: () =>
                                                            'relative-section-select_menu'
                                                    }}
                                                    isDisabled={
                                                        this.props
                                                            .sectionOptions
                                                            .length < 2
                                                    }
                                                    options={this.sectionOptionsCreation()}
                                                    value={{
                                                        value: context.sectionTag,
                                                        label: context.section
                                                    }}
                                                    onChange={option => {
                                                        // todo: use ctx request state update
                                                        const section =
                                                            option.label;
                                                        const sectionTag =
                                                            option.value;
                                                        context.loadUserPreferences(
                                                            section,
                                                            sectionTag
                                                        );
                                                        this.props.handleSection(
                                                            {
                                                                section:
                                                                    section,
                                                                sectionTag:
                                                                    sectionTag
                                                            }
                                                        );
                                                    }}
                                                />
                                            ) : (
                                                <span
                                                    className="section-name"
                                                    data-testid="section-name"
                                                >
                                                    {this.translate(
                                                        'in: {sectionLabel}',
                                                        {
                                                            sectionLabel:
                                                                sectionLabel
                                                        }
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                        <div className="input-container">
                                            <CodeEditor
                                                height="100%"
                                                data-testid="code-editor"
                                                mode={filterBarMode}
                                                setup={editorSetup}
                                                completions={editorCompletions}
                                                onUpdate={this.onUpdate}
                                                onChange={this.onChange}
                                                onCreateEditor={
                                                    this.onCreateEditor
                                                }
                                                extensions={extensions}
                                                value={
                                                    // todo: on section change, reset value
                                                    this.state.inputNewValue
                                                }
                                            />
                                        </div>
                                        {this.state.showOperatorsHelper && (
                                            <OperatorsHelper
                                                show={true}
                                                onHide={() => {
                                                    this.setState(
                                                        {
                                                            showOperatorsHelper: false
                                                        },
                                                        () => {
                                                            this.props.setActivateHelpModals(
                                                                false
                                                            );
                                                        }
                                                    );
                                                }}
                                                section={this.props.section}
                                                inputChangeHandler={item => {
                                                    this.inputChangeFromModal(
                                                        item.display
                                                    );
                                                }}
                                            />
                                        )}
                                    </div>
                                    {this.state.showMiniModal && (
                                        <MiniModal
                                            show={true}
                                            bodyClassName="date-time-body"
                                            title={this.translate(
                                                'Date & Time Helper'
                                            )}
                                            width="250px"
                                            posX="0px"
                                            posY="40px"
                                            onHide={() => {
                                                this.setState(
                                                    {
                                                        timeInterval: {
                                                            ...HDWMY
                                                        },
                                                        showMiniModal: false,
                                                        timeToggle: false
                                                    },
                                                    () => {
                                                        this.props.setActivateDateModals(
                                                            false
                                                        );
                                                        this.setState({
                                                            showMiniModal: false
                                                        });
                                                    }
                                                );
                                            }}
                                            footer={
                                                <div className="datetime-modal__footer mui-m-2">
                                                    <Button
                                                        data-testid="btn-date-filter"
                                                        sx={{ padding: 1 }}
                                                        /*
                                                        todo: define a consistent check
                                                        disabled={
                                                            key_DatePicker !== this.state.dateTimeSelection
                                                                ? !this.state.selectedTime
                                                                : !this.state.selectedDate
                                                        }
                                                        */
                                                        variant="text"
                                                        color="primary"
                                                        onClick={
                                                            this
                                                                .setDateTimeFilter
                                                        }
                                                    >
                                                        {this.translate('Set')}
                                                    </Button>
                                                </div>
                                            }
                                        >
                                            <div className="px-3 w-100">
                                                <ToggleButtons
                                                    buttons={[
                                                        [
                                                            this.translate(
                                                                key_TimeInterval
                                                            ),
                                                            () => {
                                                                this.setState({
                                                                    dateTimeSelection:
                                                                        key_TimeInterval,
                                                                    timeToggle: false
                                                                });
                                                            }
                                                        ],
                                                        [
                                                            this.translate(
                                                                key_DatePicker
                                                            ),
                                                            () => {
                                                                this.setState({
                                                                    dateTimeSelection:
                                                                        key_DatePicker,
                                                                    timeToggle: false
                                                                });
                                                            }
                                                        ]
                                                    ]}
                                                    selectedValue={
                                                        this.state.dateTimeSelection === key_DatePicker ? 1 : 0
                                                    }
                                                />
                                                {key_TimeInterval !==
                                                this.state.dateTimeSelection ? (
                                                    <>
                                                        <div className="date-picker-wrapper">
                                                            <p className="datetime-modal__text my-2 mt-3">
                                                                <strong className="mui-d-flex ">
                                                                    {this.translate(
                                                                        'Select an exact date'
                                                                    )}
                                                                </strong>
                                                            </p>

                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        slotProps={{
                                                                            input: {
                                                                                'data-testid':
                                                                                    'checkbox-exact-time',
                                                                                id: 'switch-empty-fields'
                                                                            }
                                                                        }}
                                                                        checked={
                                                                            this
                                                                                .state
                                                                                .timeToggle
                                                                        }
                                                                        onChange={() => {
                                                                            this.setState(
                                                                                {
                                                                                    timeToggle:
                                                                                        !this
                                                                                            .state
                                                                                            .timeToggle
                                                                                }
                                                                            );
                                                                        }}
                                                                    />
                                                                }
                                                                label={this.translate(
                                                                    'Time'
                                                                )}
                                                            />
                                                        </div>
                                                        <DatePicker
                                                            id="datepicker"
                                                            // data-testid="datepicker"
                                                            className="w-100 form-control form-control-sm input-border mb-4"
                                                            placeholderText={this.translate(
                                                                'Please select a date'
                                                            )}
                                                            dateFormat="yyyy/MM/dd"
                                                            showMonthDropdown
                                                            showYearDropdown
                                                            shouldCloseOnSelect={
                                                                true
                                                            }
                                                            selected={
                                                                this.state
                                                                    .selectedDate
                                                            }
                                                            onChange={date => {
                                                                this.setState({
                                                                    selectedDate:
                                                                        date
                                                                });
                                                            }}
                                                        />
                                                        {this.state
                                                            .timeToggle && (
                                                            <span
                                                                className="w-100"
                                                                style={{
                                                                    display:
                                                                        'inline-block'
                                                                }}
                                                            >
                                                                <DatePicker
                                                                    id="timepicker"
                                                                    // data-testid="timepicker"
                                                                    className="w-100 form-control form-control-sm input-border mb-3"
                                                                    placeholderText={this.translate(
                                                                        'Please select a time'
                                                                    )}
                                                                    dateFormat="hh:mm aa"
                                                                    showTimeSelectOnly
                                                                    showTimeSelect
                                                                    timeIntervals={
                                                                        15
                                                                    }
                                                                    shouldCloseOnSelect={
                                                                        true
                                                                    }
                                                                    selected={
                                                                        this
                                                                            .state
                                                                            .selectedTime
                                                                    }
                                                                    onChange={time => {
                                                                        this.setState(
                                                                            {
                                                                                selectedTime:
                                                                                    time
                                                                            }
                                                                        );
                                                                    }}
                                                                />
                                                            </span>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <p className="datetime-modal__text my-2 mt-3">
                                                            <strong>
                                                                {this.translate(
                                                                    'Include entries from the last'
                                                                )}
                                                            </strong>
                                                        </p>
                                                        {[
                                                            'Hours',
                                                            'Days',
                                                            'Weeks',
                                                            'Months',
                                                            'Years'
                                                        ].map(
                                                            (
                                                                item,
                                                                index,
                                                                intervals
                                                            ) => (
                                                                <TimeSliders
                                                                    key={
                                                                        item +
                                                                        index
                                                                    }
                                                                    timeSpan={
                                                                        item
                                                                    }
                                                                    currentInterval={
                                                                        this
                                                                            .state
                                                                            .timeInterval
                                                                    }
                                                                    onIntervalChangeHandler={(
                                                                        value,
                                                                        time
                                                                    ) => {
                                                                        if (
                                                                            intervals.includes(
                                                                                time
                                                                            )
                                                                        ) {
                                                                            const update =
                                                                                {
                                                                                    ...this
                                                                                        .state
                                                                                        .timeInterval
                                                                                };
                                                                            update[
                                                                                time.charAt(
                                                                                    0
                                                                                )
                                                                            ] =
                                                                                value;
                                                                            this.setState(
                                                                                {
                                                                                    timeInterval:
                                                                                        update
                                                                                }
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            )
                                                        )}
                                                        <div className="mt-4 time-interval-wrapper">
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        slotProps={{
                                                                            input: {
                                                                                'data-testid':
                                                                                    'checkbox-absolute-values'
                                                                            }
                                                                        }}
                                                                        checked={
                                                                            this
                                                                                .state
                                                                                .absoluteToggle ||
                                                                            false
                                                                        }
                                                                        onChange={() => {
                                                                            this.setState(
                                                                                {
                                                                                    absoluteToggle:
                                                                                        !this
                                                                                            .state
                                                                                            .absoluteToggle
                                                                                }
                                                                            );
                                                                        }}
                                                                    />
                                                                }
                                                                label={this.translate(
                                                                    'Absolute Values'
                                                                )}
                                                            />
                                                        </div>
                                                        <div className="mt-4">
                                                            <p className="datetime-modal__text mt-4 mb-1">
                                                                <strong>
                                                                    {this.translate(
                                                                        'Quick Picks'
                                                                    )}
                                                                </strong>
                                                            </p>
                                                            <div className="mt-0">
                                                                {[
                                                                    [
                                                                        '1H',
                                                                        '1 Hour Ago'
                                                                    ],
                                                                    [
                                                                        '1D',
                                                                        '1 Day Ago'
                                                                    ],
                                                                    [
                                                                        '1W',
                                                                        '1 Week Ago'
                                                                    ],
                                                                    [
                                                                        '1M',
                                                                        '1 Month Ago'
                                                                    ],
                                                                    [
                                                                        '1Y',
                                                                        '1 Year Ago'
                                                                    ]
                                                                ].map(
                                                                    (
                                                                        [
                                                                            value,
                                                                            label
                                                                        ],
                                                                        index
                                                                    ) => (
                                                                        <Badge
                                                                            pill
                                                                            key={
                                                                                index
                                                                            }
                                                                            bg="primary"
                                                                            data-testid={`quick-pick-${value}`}
                                                                            className="datetime-modal__quick-pick p-1"
                                                                            onClick={() => {
                                                                                this.quickPick(
                                                                                    value
                                                                                );
                                                                            }}
                                                                        >
                                                                            {this.translate(
                                                                                label
                                                                            )}
                                                                        </Badge>
                                                                    )
                                                                )}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </MiniModal>
                                    )}
                                </>
                            );
                        }}
                    </StickyAlertContext.Consumer>
                )}
            </PreferencesContext.Consumer>
        );
    }
}

InputBar.propTypes = {
    section: PropTypes.string,
    filterValue: PropTypes.string,
    location: PropTypes.object,
    completions: PropTypes.array,
    handleKeyPress: PropTypes.func,
    handleChange: PropTypes.func,
    setInputBarRef: PropTypes.func,
    activateHelpModals: PropTypes.bool,
    setActivateHelpModals: PropTypes.func,
    activateDateModals: PropTypes.bool,
    setActivateDateModals: PropTypes.func
};

export default withRouter(injectIntl(InputBar));
